/**
 * EPostSettings - Admin-Konfiguration für den ePost-Briefversand.
 * Rechnungen können pro Kunde automatisch als physischer Brief (Schweizer Post)
 * verschickt werden. Live-Versand ist hinter `live_mode` abgesichert.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, Save, Loader2, AlertTriangle, Users, ShieldCheck, RefreshCw, CheckCircle2, Circle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Zustell-Timeline: Eingereicht → Gedruckt → Zugestellt
const STAGES = [
  { key: 'submitted', label: 'Eingereicht' },
  { key: 'printed', label: 'Gedruckt' },
  { key: 'delivered', label: 'Zugestellt' },
];

const StatusTimeline = ({ status }) => {
  if (status === 'dry_run') {
    return <Badge variant="secondary" data-testid="job-status-dryrun">Test (Dry-Run)</Badge>;
  }
  if (status === 'error') {
    return <Badge variant="destructive" data-testid="job-status-error">Fehler</Badge>;
  }
  const reachedIdx = STAGES.findIndex((s) => s.key === status);
  const activeIdx = status === 'submitting' ? -1 : reachedIdx;
  return (
    <div className="flex items-center gap-1" data-testid="job-status-timeline">
      {STAGES.map((s, i) => {
        const done = i <= activeIdx;
        return (
          <div key={s.key} className="flex items-center gap-1">
            {done
              ? <CheckCircle2 className="h-4 w-4 text-green-500" />
              : <Circle className="h-4 w-4 text-gray-300" />}
            <span className={`text-xs ${done ? 'text-green-700 font-medium' : 'text-gray-400'}`}>{s.label}</span>
            {i < STAGES.length - 1 && <span className={`w-4 h-px ${i < activeIdx ? 'bg-green-400' : 'bg-gray-200'}`} />}
          </div>
        );
      })}
    </div>
  );
};

const EPostSettings = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eligible, setEligible] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [cfg, setCfg] = useState({
    api_key: '',
    api_key_masked: '',
    api_key_set: false,
    base_url: 'https://api.epost.ch',
    send_path: '',
    status_path: '',
    sender_id: '',
    delivery_channel: 'physical',
    job_id_field: 'id',
    enabled: false,
    live_mode: false,
  });

  const authHeader = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, e, j] = await Promise.all([
        axios.get(`${API}/admin/epost/config`, { headers: authHeader() }),
        axios.get(`${API}/admin/epost/eligible-count`, { headers: authHeader() }),
        axios.get(`${API}/admin/epost/letter-jobs`, { headers: authHeader() }),
      ]);
      setCfg((prev) => ({ ...prev, ...c.data, api_key: '' }));
      setEligible(e.data?.count ?? 0);
      setJobs(j.data?.jobs ?? []);
    } catch (err) {
      toast.error('ePost-Einstellungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      delete payload.api_key_masked;
      delete payload.api_key_set;
      if (!payload.api_key) delete payload.api_key; // leer → Key behalten
      const res = await axios.put(`${API}/admin/epost/config`, payload, { headers: authHeader() });
      setCfg((prev) => ({ ...prev, ...res.data, api_key: '' }));
      toast.success('ePost-Einstellungen gespeichert');
    } catch (err) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const refreshStatuses = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/admin/epost/refresh-statuses`, {}, { headers: authHeader() });
      const j = await axios.get(`${API}/admin/epost/letter-jobs`, { headers: authHeader() });
      setJobs(j.data?.jobs ?? []);
      if (res.data?.skipped_reason) {
        toast.info('Status-Abfrage nur im Live-Modus mit Status-Pfad möglich');
      } else {
        toast.success(`Status aktualisiert (${res.data?.updated ?? 0} von ${res.data?.checked ?? 0} geändert)`);
      }
    } catch (e) {
      toast.error('Status-Aktualisierung fehlgeschlagen');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-red-500" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="epost-settings-page">
      <div className="flex items-center gap-3">
        {!embedded && (
          <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} data-testid="epost-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Mail className="h-6 w-6 text-red-500" /> ePost Briefversand
        </h1>
      </div>

      {/* Status-Übersicht */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <Users className="h-8 w-8 text-red-500" />
            <div>
              <div className="text-2xl font-bold" data-testid="epost-eligible-count">{eligible}</div>
              <div className="text-sm text-gray-500">Kunden mit ePost-Versand</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <ShieldCheck className={`h-8 w-8 ${cfg.live_mode ? 'text-green-500' : 'text-amber-500'}`} />
            <div>
              <div className="text-lg font-bold">{cfg.live_mode ? 'Live' : 'Test (Dry-Run)'}</div>
              <div className="text-sm text-gray-500">Versand-Modus</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <Mail className={`h-8 w-8 ${cfg.enabled ? 'text-green-500' : 'text-gray-400'}`} />
            <div>
              <div className="text-lg font-bold">{cfg.enabled ? 'Aktiv' : 'Inaktiv'}</div>
              <div className="text-sm text-gray-500">ePost-Integration</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live-Warnung */}
      {!cfg.send_path && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="epost-contract-warning">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-800">
            <strong>Vor dem Live-Versand:</strong> ePost stellt den genauen Versand-Endpfad (Submit-Path)
            und das Payload-Schema nur im Onboarding-Paket (OpenAPI/Postman) bereit. Trage den
            „Submit-Path" unten aus deinen ePost-Unterlagen ein und aktiviere erst dann den Live-Modus.
            Solange der Live-Modus aus ist, wird <strong>kein echter Brief</strong> versendet – die Rechnungen
            gehen wie bisher per E-Mail/WhatsApp und es wird nur ein Test-Eintrag erstellt.
          </div>
        </div>
      )}

      {/* Konfiguration */}
      <Card>
        <CardHeader><CardTitle>Zugangsdaten & Endpunkte</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>API-Key (ePost oneAPI)</Label>
            <Input
              type="password"
              placeholder={cfg.api_key_set ? `Gespeichert: ${cfg.api_key_masked} (leer lassen zum Behalten)` : 'API-Key eingeben'}
              value={cfg.api_key}
              onChange={(e) => setCfg({ ...cfg, api_key: e.target.value })}
              data-testid="epost-api-key-input"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Base-URL</Label>
              <Input value={cfg.base_url} onChange={(e) => setCfg({ ...cfg, base_url: e.target.value })} data-testid="epost-base-url-input" />
            </div>
            <div>
              <Label>Sender-ID</Label>
              <Input value={cfg.sender_id} onChange={(e) => setCfg({ ...cfg, sender_id: e.target.value })} placeholder="aus ePost-Konto" data-testid="epost-sender-id-input" />
            </div>
            <div>
              <Label>Submit-Path (aus ePost OpenAPI)</Label>
              <Input value={cfg.send_path} onChange={(e) => setCfg({ ...cfg, send_path: e.target.value })} placeholder="z.B. communication/v1/letters" data-testid="epost-send-path-input" />
            </div>
            <div>
              <Label>Status-Path (optional)</Label>
              <Input value={cfg.status_path} onChange={(e) => setCfg({ ...cfg, status_path: e.target.value })} placeholder="z.B. communication/v1/letters/{id}" data-testid="epost-status-path-input" />
            </div>
            <div>
              <Label>Kanal (physisch)</Label>
              <Input value={cfg.delivery_channel} onChange={(e) => setCfg({ ...cfg, delivery_channel: e.target.value })} data-testid="epost-channel-input" />
            </div>
            <div>
              <Label>Job-ID-Feld in Antwort</Label>
              <Input value={cfg.job_id_field} onChange={(e) => setCfg({ ...cfg, job_id_field: e.target.value })} data-testid="epost-jobid-field-input" />
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label>ePost-Integration aktiv</Label>
              <p className="text-sm text-gray-500">Schaltet die Integration grundsätzlich ein.</p>
            </div>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="epost-enabled-switch" />
          </div>
          <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
            <div>
              <Label className="text-red-800">Live-Versand (echte Briefe!)</Label>
              <p className="text-sm text-red-600">AN = echte physische Briefe werden versendet (kostenpflichtig). AUS = Test/Dry-Run.</p>
            </div>
            <Switch checked={cfg.live_mode} onCheckedChange={(v) => setCfg({ ...cfg, live_mode: v })} data-testid="epost-live-mode-switch" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="epost-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Speichern
        </Button>
      </div>

      {/* Letzte Versand-Jobs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Zustellstatus je Rechnung</CardTitle>
          <Button variant="outline" size="sm" onClick={refreshStatuses} disabled={refreshing} data-testid="epost-refresh-status-btn">
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Status aktualisieren
          </Button>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="epost-no-jobs">Noch keine Versand-Vorgänge.</p>
          ) : (
            <div className="space-y-2" data-testid="epost-jobs-list">
              {jobs.slice(0, 50).map((j, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 border rounded-lg text-sm">
                  <div>
                    <div className="font-medium">{j.recipient?.name || j.customer_id}</div>
                    <div className="text-gray-500">Rechnung {j.invoice_id} · {j.billing_month}{j.status_updated_at ? ` · Stand: ${new Date(j.status_updated_at).toLocaleString('de-CH')}` : ''}</div>
                  </div>
                  <StatusTimeline status={j.status} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EPostSettings;
